import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  IconButton,
  MenuItem,
  Menu,
} from "@mui/material";
import {
  Person,
  Class,
  Event,
  AdminPanelSettings,
  MoreHoriz,
  Update,
  AttachMoney,
  ManageHistory,
  AutoStoriesOutlined,
} from "@mui/icons-material";
import { formatDateOutput } from "../../../Utils/formatDate";
import "../../../Styles/dashboard.css";
import UpdateInfo from "./Info/UpdateInfo";
import AttachFees from "./Fee/AttachFees";
import FeesHistory from "./Fee/FeesHistory";
import BookFeeHistory from "./BookHistory/BookFeeHistory";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  {
    id: "registrationId",
    label: "Registration Id",
    icon: <Class sx={{ marginRight: 1 }} />,
  },
  { id: "name", label: "Name", icon: <Person sx={{ marginRight: 1 }} /> },
  { id: "class", label: "Class", icon: <Class sx={{ marginRight: 1 }} /> },

  {
    id: "dob",
    label: "Date of Birth",
    icon: <Event sx={{ marginRight: 1 }} />,
  },
  {
    id: "doj",
    label: "Date of Joining",
    icon: <Event sx={{ marginRight: 1 }} />,
  },
  {
    id: "actions",
    label: "Actions",
    icon: <AdminPanelSettings sx={{ marginRight: 1 }} />,
  },
];

function StudentsTable({
  students,
  classes,
  selectedClassId,
  handleActionClick,
  setActionComponent,
  fetchStudentsByClassId,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [paginatedData, setPaginatedData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentStudentId, setCurrentStudentId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(students?.data);
  }, [students]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentStudentId(null);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const actionsMenu = [
    {
      label: "Update Info",
      icon: <Update fontSize="small" sx={{ marginRight: 1 }} />,
      onClick: () => {
        const actionComponent = (
          <UpdateInfo
            setActionComponent={setActionComponent}
            selectedStudentId={selectedStudentId}
            students={students}
            fetchStudentsByClassId={fetchStudentsByClassId}
          />
        );
        handleActionClick(actionComponent);
      },
    },

    {
      label: "Attach Fees",
      icon: <AttachMoney fontSize="small" sx={{ marginRight: 1 }} />,
      onClick: () => {
        const actionComponent = (
          <AttachFees
            // studentName={studentName}
            setActionComponent={setActionComponent}
            selectedStudentId={selectedStudentId}
          />
        );
        handleActionClick(actionComponent);
      },
    },

    {
      label: "Show Fee History",
      icon: <ManageHistory fontSize="small" sx={{ marginRight: 1 }} />,
      onClick: () => {
        const actionComponent = (
          <FeesHistory
            setActionComponent={setActionComponent}
            selectedStudentId={selectedStudentId}
            studentName={studentName}
          />
        );
        handleActionClick(actionComponent);
      },
    },
    {
      label: "Book Fee History",
      icon: <AutoStoriesOutlined fontSize="small" sx={{ marginRight: 1 }} />,
      onClick: () => {
        const actionComponent = (
          <BookFeeHistory
            setActionComponent={setActionComponent}
            selectedStudentId={selectedStudentId}
            studentName={studentName}
            classes={classes}
            selectedClassId={selectedClassId}
          />
        );
        handleActionClick(actionComponent);
      },
    },
  ];

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell>{row?.registrationId}</TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {`${row?.firstName} ${
                          row?.middleName ? row?.middleName + " " : ""
                        }${row?.lastName}`}
                      </TableCell>
                      <TableCell className="table-cell">
                        {classes?.data?.find(
                          (classItem) => classItem?.id === row?.classId
                        )
                          ? `${
                              classes?.data?.find(
                                (classItem) => classItem?.id === row?.classId
                              ).grade
                            } - ${
                              classes?.data?.find(
                                (classItem) => classItem?.id === row?.classId
                              ).section
                            }`
                          : "Class not found"}
                      </TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.dob)}
                      </TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.doj)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setCurrentStudentId(row?.id);
                            setSelectedStudentId(row?.id);
                            setStudentName(
                              `${row?.firstName} ${
                                row?.middleName ? row?.middleName + " " : ""
                              }${row?.lastName}`
                            );
                          }}
                        >
                          <MoreHoriz />
                        </IconButton>
                        <Menu
                          id="actions-menu"
                          anchorEl={anchorEl}
                          open={Boolean(
                            anchorEl && currentStudentId === row.id
                          )}
                          onClose={handleClose}
                        >
                          {actionsMenu.map((action, index) => (
                            <MenuItem key={index} onClick={action.onClick}>
                              {action.icon}
                              {action.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

StudentsTable.propTypes = {
  students: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  handleActionClick: PropTypes.func.isRequired,
  fetchStudentsByClassId: PropTypes.func.isRequired,
};

export default StudentsTable;
